import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { FleetrifyModal } from "../Modal/modal";
import { useChangePinMutation } from "../../redux/services/Wallet";
import { toast } from "sonner";

const WalletPinManagement = () => {
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [resetEmail, setResetEmail] = useState("");

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmationError, setConfirmationError] = useState("");

  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [otp, setOtp] = useState("");

  const [isSetupPinModalOpen, setIsSetupPinModalOpen] = useState(false);

  const [errors, setErrors] = useState<{
    currentPin?: string;
    newPin?: string;
    confirmPin?: string;
    resetEmail?: string;
  }>({});

  const [changePin, { isLoading: changingPin }] = useChangePinMutation();

  // ====== Handlers ======

  const handlePinChange = (
    field: "currentPin" | "newPin" | "confirmPin",
    value: string
  ) => {
    // Only allow numeric input
    if (value === "" || /^\d+$/.test(value)) {
      if (field === "currentPin") setCurrentPin(value);
      if (field === "newPin") setNewPin(value);
      if (field === "confirmPin") setConfirmPin(value);

      // Clear error
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    }
  };

  const handleEmailChange = (value: string) => {
    setResetEmail(value);
    if (errors.resetEmail) {
      setErrors((prev) => ({ ...prev, resetEmail: undefined }));
    }
  };

  // ====== Validations ======

  const validatePinField = (field: "currentPin" | "newPin" | "confirmPin") => {
    let value = "";
    if (field === "currentPin") value = currentPin;
    if (field === "newPin") value = newPin;
    if (field === "confirmPin") value = confirmPin;

    if (value.trim() === "") {
      setErrors((prev) => ({ ...prev, [field]: "PIN is required" }));
      return false;
    }

    if (value.length !== 4) {
      setErrors((prev) => ({
        ...prev,
        [field]: "PIN must be exactly 4 digits",
      }));
      return false;
    }

    if (field === "confirmPin" && value !== newPin) {
      setErrors((prev) => ({ ...prev, confirmPin: "PINs do not match" }));
      return false;
    }

    setErrors((prev) => ({ ...prev, [field]: undefined }));
    return true;
  };

  const validateEmail = () => {
    if (resetEmail.trim() === "") {
      setErrors((prev) => ({ ...prev, resetEmail: "Email is required" }));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setErrors((prev) => ({
        ...prev,
        resetEmail: "Please enter a valid email address",
      }));
      return false;
    }

    setErrors((prev) => ({ ...prev, resetEmail: undefined }));
    return true;
  };

  // ====== Change PIN ======

 const handleUpdatePin = async () => {
   const isCurrentPinValid = validatePinField("currentPin");
   const isNewPinValid = validatePinField("newPin");
   const isConfirmPinValid = validatePinField("confirmPin");

   if (isCurrentPinValid && isNewPinValid && isConfirmPinValid) {
     try {
       const res = await changePin({
         old_pin: currentPin,
         new_pin: newPin,
       }).unwrap();


      // ✅ Safely extract success message
             const message =
               res?.data?.message ||
               res?.error?.message ||
               "Wallet PIN changed successfully.";

       toast.success(message);

       setCurrentPin("");
       setNewPin("");
       setConfirmPin("");
     } catch (err: any) {
       console.error("❌ Failed to change PIN:", err);

       // ✅ Safely extract error message
       const message =
         err?.data?.error?.message ||
         err?.data?.message ||
         "Failed to change wallet PIN.";

       toast.error(message);
     }
   }
 };



  const handleConfirmPinUpdate = () => {
    if (!password || password.trim() === "") {
      setConfirmationError("Please enter your password to confirm");
      return;
    }


    setIsConfirmModalOpen(false);
    setCurrentPin("");
    setNewPin("");
    setConfirmPin("");
    setPassword("");
  };

  // ====== Reset PIN ======

  const handleResetPin = () => {
    if (validateEmail()) {
      setIsResetModalOpen(true);
    }
  };

  const handleOtpSubmit = () => {
    if (otp.length === 6) {
      setIsResetModalOpen(false);
      setIsSetupPinModalOpen(true);
    }
  };

  const isUpdatePinFormValid = () => {
    return (
      currentPin.length === 4 &&
      newPin.length === 4 &&
      confirmPin.length === 4 &&
      newPin === confirmPin
    );
  };

  // ====== Render ======

  return (
    <div className="p-6 space-y-8">
      {/* Change Wallet PIN Section */}
      <div>
        <h3 className="text-[16px] md:text-[20px] font-semibold text-fleetblue mb-1">
          Change Wallet PIN
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          Enter your current and new wallet PIN below.
        </p>

        {/* Current PIN */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-fleetblue mb-2">
            Current PIN<span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showCurrentPin ? "text" : "password"}
              value={currentPin}
              onChange={(e) => handlePinChange("currentPin", e.target.value)}
              onBlur={() => validatePinField("currentPin")}
              placeholder="4-digit input number only"
              maxLength={4}
              inputMode="numeric"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm outline-none"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPin(!showCurrentPin)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showCurrentPin ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.currentPin && (
            <p className="text-red-500 text-sm mt-1">{errors.currentPin}</p>
          )}
        </div>

        {/* New and Confirm PIN */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* New PIN */}
          <div>
            <label className="block text-sm font-medium text-fleetblue mb-2">
              New PIN<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showNewPin ? "text" : "password"}
                value={newPin}
                onChange={(e) => handlePinChange("newPin", e.target.value)}
                onBlur={() => validatePinField("newPin")}
                placeholder="4-digit input number only"
                maxLength={4}
                inputMode="numeric"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm outline-none"
              />
              <button
                type="button"
                onClick={() => setShowNewPin(!showNewPin)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPin ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.newPin && (
              <p className="text-red-500 text-sm mt-1">{errors.newPin}</p>
            )}
          </div>

          {/* Confirm New PIN */}
          <div>
            <label className="block text-sm font-medium text-fleetblue mb-2">
              Confirm New PIN<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPin ? "text" : "password"}
                value={confirmPin}
                onChange={(e) => handlePinChange("confirmPin", e.target.value)}
                onBlur={() => validatePinField("confirmPin")}
                placeholder="4-digit input number only"
                maxLength={4}
                inputMode="numeric"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm outline-none"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPin(!showConfirmPin)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPin ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.confirmPin && (
              <p className="text-red-500 text-sm mt-1">{errors.confirmPin}</p>
            )}
          </div>
        </div>

        <button
          onClick={handleUpdatePin}
          disabled={!isUpdatePinFormValid() || changingPin}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
            isUpdatePinFormValid()
              ? "bg-fleetblue text-white"
              : "bg-[#B4C0D8] text-[#D0D7E7] cursor-not-allowed"
          }`}
        >
          {changingPin ? "Updating..." : "Update PIN"}
        </button>
      </div>

      {/* Reset Wallet PIN Section */}
      {/* <div>
        <h3 className="text-[16px] md:text-[20px] font-semibold text-fleetblue mb-1">
          Reset Wallet PIN
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          Enter your registered email to receive a reset code.
        </p>

        <div className="mb-6">
          <label className="block text-sm font-medium text-fleetblue mb-2">
            Email<span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="email"
              value={resetEmail}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={validateEmail}
              placeholder="example@gmail.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm outline-none"
            />
            <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
          {errors.resetEmail && (
            <p className="text-red-500 text-sm mt-1">{errors.resetEmail}</p>
          )}
        </div>

        <button
          onClick={handleResetPin}
          disabled={!resetEmail.trim()}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
            resetEmail.trim()
              ? "bg-fleetblue text-white"
              : "bg-[#B4C0D8] text-[#D0D7E7] cursor-not-allowed"
          }`}
        >
          Send Reset Code
        </button>
      </div> */}

      {/* ====== Modals ====== */}

      {/* Confirm PIN Update Modal */}
      {/* <FleetrifyModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        title="Confirm Update"
        className="max-w-md h-fit"
      >
        <div className="space-y-4 p-6">
          <h3 className="text-base font-semibold text-fleetblue mb-2">
            Enter Account Password
          </h3>

          <label className="block text-sm font-medium text-fleetblue mb-2">
            Enter Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none"
          />
          {confirmationError && (
            <p className="text-red-500 text-sm mt-1">{confirmationError}</p>
          )}

          <button
            onClick={handleConfirmPinUpdate}
            disabled={!password}
            className={`w-auto py-3 px-6 rounded-lg transition-colors font-medium ${
              password
                ? "bg-fleetblue text-white"
                : "bg-[#B4C0D8] text-[#D0D7E7] cursor-not-allowed"
            }`}
          >
            Confirm Change
          </button>
        </div>
      </FleetrifyModal> */}

      {/* OTP Modal */}
      <FleetrifyModal
        isOpen={isResetModalOpen}
        onClose={() => {
          setIsResetModalOpen(false);
          setOtp("");
        }}
        title="Confirm OTP Code"
        className="max-w-md h-fit"
      >
        <div className="space-y-4 p-6">
          <h3 className="text-base font-semibold text-gray-800 mb-2">
            Confirm the OTP code sent to your email address.
          </h3>

          <label className="block text-sm font-medium text-fleetblue mb-2">
            OTP <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={otp}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || /^\d+$/.test(value)) setOtp(value);
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none"
            inputMode="numeric"
            maxLength={6}
            placeholder="Enter 6-digit code"
          />

          <button
            onClick={handleOtpSubmit}
            disabled={otp.length !== 6}
            className={`w-auto py-3 px-6 rounded-lg transition-colors font-medium ${
              otp.length === 6
                ? "bg-fleetblue text-white"
                : "bg-[#B4C0D8] text-[#D0D7E7] cursor-not-allowed"
            }`}
          >
            Confirm Code
          </button>
        </div>
      </FleetrifyModal>

      {/* Setup New PIN Modal */}
      <FleetrifyModal
        isOpen={isSetupPinModalOpen}
        onClose={() => {
          setIsSetupPinModalOpen(false);
          setNewPin("");
          setConfirmPin("");
        }}
        title="Reset Wallet PIN"
        className="max-w-md h-fit"
      >
        <div className="p-6 space-y-5">
          <h3 className="text-base font-semibold text-fleetblue">
            Setup Wallet PIN
          </h3>
          <p className="text-sm text-gray-500">
            Set a 4-digit PIN. This will be required to authorize transfers,
            withdrawals, and sensitive wallet actions.
          </p>

          {/* New PIN */}
          <div>
            <label className="block text-sm font-medium text-fleetblue mb-2">
              New PIN:<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showNewPin ? "text" : "password"}
                value={newPin}
                onChange={(e) => handlePinChange("newPin", e.target.value)}
                maxLength={4}
                inputMode="numeric"
                placeholder="Enter new PIN"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
              />
              <button
                type="button"
                onClick={() => setShowNewPin(!showNewPin)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPin ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm New PIN */}
          <div>
            <label className="block text-sm font-medium text-fleetblue mb-2">
              Confirm New PIN:<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPin ? "text" : "password"}
                value={confirmPin}
                onChange={(e) => handlePinChange("confirmPin", e.target.value)}
                maxLength={4}
                inputMode="numeric"
                placeholder="Re-enter new PIN"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPin(!showConfirmPin)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPin ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <button
            disabled={
              !newPin ||
              !confirmPin ||
              newPin.length !== 4 ||
              confirmPin.length !== 4 ||
              newPin !== confirmPin
            }
            onClick={() => {
              setIsSetupPinModalOpen(false);
            }}
            className={`w-auto py-3 px-6 rounded-lg transition-colors font-medium ${
              newPin &&
              confirmPin &&
              newPin === confirmPin &&
              newPin.length === 4
                ? "bg-fleetblue text-white"
                : "bg-[#B4C0D8] text-[#D0D7E7] cursor-not-allowed"
            }`}
          >
            Update Pin
          </button>
        </div>
      </FleetrifyModal>
    </div>
  );
};

export default WalletPinManagement;
